import { Button, Loader } from '@mantine/core';
import { ReactNode } from 'react';

export type StyledButtonVariant =
  | 'primary'
  | 'secondary'
  | 'success'
  | 'error'
  | 'ghost';

export type StyledButtonSize = 'small' | 'medium' | 'large';

export type StyledButtonProps = {
  variant?: StyledButtonVariant;
  size?: StyledButtonSize;
  loading?: boolean;
  fullWidthMobile?: boolean;
  disabled?: boolean;
  children?: ReactNode;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  startIcon?: ReactNode;
  endIcon?: ReactNode;
  type?: 'button' | 'submit' | 'reset';
  style?: React.CSSProperties;
  sx?: React.CSSProperties;
  className?: string;
  fullWidth?: boolean;
  href?: string;
  target?: string;
  component?: any;
  to?: string;
};

const variantMap: Record<
  StyledButtonVariant,
  { mantineVariant: string; color: string }
> = {
  primary: { mantineVariant: 'filled', color: 'violet' },
  secondary: { mantineVariant: 'outline', color: 'violet' },
  success: { mantineVariant: 'filled', color: 'green' },
  error: { mantineVariant: 'filled', color: 'red' },
  ghost: { mantineVariant: 'subtle', color: 'violet' },
};

const sizeMap: Record<StyledButtonSize, string> = {
  small: 'xs',
  medium: 'sm',
  large: 'md',
};

export const StyledButton = ({
  variant = 'primary',
  size = 'medium',
  loading = false,
  fullWidthMobile = false,
  disabled,
  children,
  startIcon,
  endIcon,
  sx,
  style,
  ...props
}: StyledButtonProps) => {
  const mapped = variantMap[variant] || variantMap.primary;
  const mantineSize = sizeMap[size] || 'sm';

  return (
    <Button
      {...props}
      variant={mapped.mantineVariant as any}
      color={mapped.color}
      size={mantineSize}
      disabled={disabled || loading}
      leftSection={loading ? undefined : startIcon}
      rightSection={endIcon}
      style={{
        minWidth: size === 'large' ? 140 : size === 'medium' ? 100 : 80,
        ...sx,
        ...style,
      }}
    >
      {loading ? <Loader size={20} color="currentColor" /> : children}
    </Button>
  );
};
